import React from "react";

import { Settings as LuxonSettings } from "luxon";

import { apiPost, apiSetToken, setApiTarget } from "../Api";
import { ApiError } from "../ApiError";
import { VERSION, VERSION_BUILD } from "../version";

export interface IAuthContext {
    isAuthenticated: boolean;
    forcePasswordChange: boolean;

    user: {
        username: string;
        displayName: string;
        employee: {
            id: string;
            locationId: string;
            tags: string[];
            groups: Array<{ id: string, name: string }>;
        };
    };
    rights: string[];
    serverType?: string;
    serverName?: string;
    authTarget: string;
    minimumVersion: string;
    detectedLocationId?: string;

    hasRight: (claim: string) => boolean;
    hasTag: (tag: string) => boolean;
    isInGroup: (group: string) => boolean;
    setUser: (authInfo: any) => void;
    authUser: (authInfo: any) => PromiseLike<void>;
    logoutUser: () => PromiseLike<void>;
}

export interface IAuthProps {
    authContext: IAuthContext;
}

interface IAuthenticationRootState {
    context: IAuthContext;
    isAuthChecked: boolean;
    networkFailure: boolean;
    isTakingLong: boolean;
}

export const WellKnownGroups = {
    Marketer: "4621580542198677504",
    MarketerExtra: "4626601197655031808",
    FrontDesk: "4621580542710644736",
    DefaultCalendarEveryone: "4624431121709400064", // TODO: Replace this with user preferences for defaults
    Managers: "4629935199246090240",

    Provider: "4621580542859018240",
    ProviderStats: "4621580542995333120",
    AnnualProvider: "4621580543136890880",
    UroTechnician: "4621580544039976960",
    PelletTechnician: "4621581409985495040",

    Surgeon: "4621580544661782528",
    ORCirculator: "4621580544810156032",
    ORScrubTech: "4621580544940179456",
    ORAnesthistist: "4621580545121058816",

    ClinicalOutcomes: "4621580545340473344",
    SurgicalOutcomes: "4621580545457389568",
    MarketingManager: "4621581101276069888",
    ClinicalOutcomesVacation: "4623704503129473024",

    HfTeam: "4628142414865629184",
    PatientCareRep: "4628142426601553920",
    PatientCareRepLeadInput: "4629932374034743296",
    AppointmentSpecialist: "4628142443628855296",
    SurgicalConsultant: "4628142437466374144",
    BetaOutreach: "4638810445271531520",

    lookupTechnician: (type: string) => {
        switch (type) {
            case "uro": return WellKnownGroups.UroTechnician;
            case "pellet": return WellKnownGroups.PelletTechnician;
            default: return undefined;
        }
    },

    lookupProvider: (type: string) => {
        switch (type) {
            case "annual": return WellKnownGroups.AnnualProvider;
            default: return WellKnownGroups.Provider;
        }
    },
};

export const AuthContext = React.createContext<IAuthContext>({
    isAuthenticated: false,
    forcePasswordChange: false,
    authTarget: "",
    user: {
        username: "",
        displayName: "",
        employee: {
            id: "",
            locationId: "",
            tags: [],
            groups: [],
        }
    },
    minimumVersion: "0.0.0",
    rights: [],
    hasRight: c => false,
    hasTag: t => false,
    isInGroup: g => false,
    setUser: u => { /**/ },
    authUser: () => Promise.reject(),
    logoutUser: () => Promise.reject()
});

class AuthenticationRoot extends React.Component<{}, IAuthenticationRootState> {
    private authIdentity: any;
    private slowTimer?: number;
    private authBroadcast?: BroadcastChannel;

    constructor(props: Readonly<{}>) {
        super(props);

        this.state = {
            context: {
                isAuthenticated: false,
                forcePasswordChange: false,
                rights: [],
                user: { username: "", displayName: "", employee: { id: "", locationId: "", tags: [], groups: [] } },
                authTarget: "",
                minimumVersion: "0.0.0",

                authUser: this.authUser.bind(this),
                hasRight: this.hasRight.bind(this),
                hasTag: this.hasTag.bind(this),
                isInGroup: this.isInGroup.bind(this),
                logoutUser: this.logoutUser.bind(this),
                setUser: this.setUser.bind(this),
            },
            isAuthChecked: false,
            isTakingLong: false,
            networkFailure: false,
        };

        this.handleMessage = this.handleMessage.bind(this);
        this.handleAuthMessage = this.handleAuthMessage.bind(this);

        if ('BroadcastChannel' in window.self) {
            this.authBroadcast = new BroadcastChannel("auth_ctx");
            this.authBroadcast.onmessage = this.handleAuthMessage;
        }
    }

    public async componentDidMount() {
        window.addEventListener("message", this.handleMessage, false);
        this.slowTimer = window.setTimeout(() => this.setState({ isTakingLong: true }), 10000);

        const token = window.localStorage.getItem("auth_token");
        const serverTarget = window.localStorage.getItem("auth_target");
        if (!token) {
            // No auth, clear user info
            this.setUser();
            return;
        }

        if (this.authBroadcast && await this.requestAuth()) {
            return;
        }

        if (window.opener) {
            // Request identity from parent window
            window.opener.postMessage({ action: "auth_request" }, "*");
        }
        else {
            this.authWithToken(token, serverTarget);
        }
    }

    public componentWillUnmount() {
        window.removeEventListener("message", this.handleMessage, false);
        if (this.slowTimer) {
            window.clearTimeout(this.slowTimer);
        }
    }

    public componentDidUpdate(prevProps: Readonly<{}>, prevState: Readonly<IAuthenticationRootState>) {
        if (!prevState.isAuthChecked && this.state.isAuthChecked) {
            if (this.slowTimer) {
                window.clearTimeout(this.slowTimer);
                this.slowTimer = undefined;
            }
        }
    }

    public render() {
        if (!this.state.isAuthChecked) {
            // Show the same initial loading spinner
            return (
                <div className="pl">
                    <div className="pl-spin" />
                    <strong>Authenticating</strong>
                    {this.state.isTakingLong ? <div><small>This is taking longer than usual...</small></div> : ""}
                </div>
            );
        }
        if (this.state.networkFailure) {
            return (
                <div className="pl">
                    <div>
                        <svg viewBox="-5 0 50 40" style={{ width: "100px" }}>
                            <path d="M 10,13 L 30,33 M 30,13 L 10,33" stroke="#f00" strokeWidth="6px" />
                            <path d="M -5,0 L 45,0 45,40 -5,40 Z" stroke="#407bbf" strokeWidth="2px" fill="transparent" />
                            <path d="M -5,6 L 45,6 45,0 -5,0 Z" fill="#407bbf" />
                        </svg>
                    </div>
                    <br />
                    <span className="error-text1">Could not contact server.</span>
                    <span className="error-text1">Please try again later.</span>
                    <br />
                    <br />
                    <div>
                        <button type="button" onClick={this.refreshPage}>Refresh</button>
                    </div>
                </div>
            );
        }

        return (
            <AuthContext.Provider value={this.state.context}>
                {this.props.children}
            </AuthContext.Provider>
        );
    }

    private refreshPage() {
        document.location.reload();
    }

    private authWithToken(token: string, serverTarget: string | null) {
        // Reauth with stored token
        this.authUser({ token, serverTarget })
            .catch(e => {
                // For now, send user back to login page
                this.setState({
                    isAuthChecked: true,
                    networkFailure: e.statusCode !== 401
                });
            });
    }

    private hasRight(right: string) {
        return !!this.state.context.rights && this.state.context.rights.some(c => c === right);
    }

    private hasTag(tag: string) {
        return !!this.state.context.user &&
            !!this.state.context.user.employee &&
            !!this.state.context.user.employee.tags &&
            this.state.context.user.employee.tags.some(t => t === tag);
    }

    private isInGroup(group: string) {
        return !!this.state.context.user &&
            !!this.state.context.user.employee &&
            !!this.state.context.user.employee.groups &&
            this.state.context.user.employee.groups.some(g => g.id === group);
    }

    private setUser(authInfo?: any) {
        if (!authInfo) {
            this.logoutUser();
            return;
        }

        // Store auth token in localStorage
        window.localStorage.setItem("auth_token", authInfo.auth.token);
        this.authIdentity = authInfo;

        LuxonSettings.defaultZoneName = authInfo.timezone || "America/New_York";
        LuxonSettings.throwOnInvalid = true;

        if (this.authBroadcast) {
            this.authBroadcast.postMessage({
                type: "auth_response",
                identity: this.authIdentity
            });
        }

        apiSetToken(authInfo.auth.token);
        this.setState(s => ({
            context: {
                isAuthenticated: true,
                forcePasswordChange: authInfo.mustChangePassword,
                rights: authInfo.auth.rights,
                serverType: authInfo.serverType,
                serverName: authInfo.serverName,
                user: {
                    displayName: authInfo.displayName,
                    employee: authInfo.employee,
                    username: authInfo.username
                },
                authTarget: authInfo.authTarget,
                minimumVersion: authInfo.minimumAppVersion || "0.0.0",
                detectedLocationId: authInfo.detectedLocationId,

                authUser: s.context.authUser,
                hasRight: s.context.hasRight,
                hasTag: s.context.hasTag,
                isInGroup: s.context.isInGroup,
                logoutUser: s.context.logoutUser,
                setUser: s.context.setUser,
            },
            isAuthChecked: true
        }));
    }

    private async authUser(inputAuthInfo: any) {
        if (!inputAuthInfo) {
            return;
        }

        // Use alternate API, if applicable
        setApiTarget(inputAuthInfo.serverTarget ? `api-${inputAuthInfo.serverTarget}` : undefined);
        window.localStorage.setItem("auth_target", inputAuthInfo.serverTarget || "");

        if (inputAuthInfo === undefined) {
            throw new Error("Must specify username and password");
        }

        let authInfo;
        if (inputAuthInfo.username) {
            // Username/password auth
            authInfo = {
                type: "password",
                version: VERSION,
                versionBuild: VERSION_BUILD,

                password: inputAuthInfo.password,
                username: inputAuthInfo.username,
            };
        }
        else if (inputAuthInfo.token) {
            // Token reauth
            authInfo = {
                type: "token",
                version: VERSION,
                versionBuild: VERSION_BUILD,

                token: inputAuthInfo.token
            }
        }
        else {
            throw new Error("Username is required");
        }

        try {
            const json = await apiPost<any>("auth/login", authInfo);
            json.authTarget = inputAuthInfo.serverTarget;

            this.setUser(json);
        }
        catch (e) {
            switch (e.statusCode) {
                case 401:
                    throw new ApiError(e.statusCode, "Incorrect username or password");
                case 400:
                    throw e; // Pass along error message
                case undefined:
                    throw new Error("Could not connect to server. Please try again later");
                default:
                    throw new Error("Could not log in, please try again later");
            }
        }
    }

    private async logoutUser(skipLogoff = false) {
        if (!skipLogoff) {
            try {
                if (this.state.context.isAuthenticated) {
                    if (this.authBroadcast) {
                        // Log out other instances
                        this.authBroadcast.postMessage({
                            type: "auth_logout"
                        });
                    }

                    await apiPost("auth/logout");
                }
            }
            catch (e) {
                // Could be having connectivity issues
            }
        }

        window.localStorage.removeItem("auth_token");
        window.localStorage.removeItem("auth_target");
        apiSetToken("");

        this.setState(s => ({
            context: {
                isAuthenticated: false,
                forcePasswordChange: false,
                rights: [],
                serverType: undefined,
                user: {
                    displayName: "",
                    employee: { id: "", locationId: "", tags: [], groups: [] },
                    username: "",
                },
                authTarget: "",
                minimumVersion: "0.0.0",
                detectedLocationId: undefined,

                authUser: s.context.authUser,
                hasRight: s.context.hasRight,
                hasTag: s.context.hasTag,
                isInGroup: s.context.isInGroup,
                logoutUser: s.context.logoutUser,
                setUser: s.context.setUser,
            },
            isAuthChecked: true,
        }));
    }

    private handleMessage(e: MessageEvent) {
        if (e.origin !== document.location!.origin ||
            !e.source ||
            e.source instanceof ServiceWorker ||
            e.source instanceof MessagePort) {
            return;
        }

        const message = e.data;
        if (message.action === "auth_request") {
            // Dialog requesting identity
            e.source.postMessage({ action: "auth_response", identity: this.authIdentity }, "*");
        }
        else if (message.action === "auth_response") {
            // Auth response
            this.setUser(message.identity);
        }
    }

    private handleAuthMessage(e: MessageEvent) {
        if (!e.data || !e.data.type) {
            return;
        }

        const msg = e.data;
        switch (msg.type) {
            case "auth_request":
                // Another instance is asking for identity
                this.authBroadcast!.postMessage({
                    type: "auth_response",
                    identity: this.authIdentity
                });
                break;
            case "auth_response":
                // Another instance is giving us auth info
                if (!this.state.context.isAuthenticated) {
                    this.setUser(msg.identity);
                }
                break;
            case "auth_logout":
                // Another instance logged out; log out here too
                this.logoutUser(true);
                break;
            default:
                break;
        }
    }

    private requestAuth(): PromiseLike<boolean> {
        return new Promise((resolve, reject) => {
            // Ask existing window(s) (if any) for auth
            this.authBroadcast!.postMessage({
                type: "auth_request"
            });
            window.setTimeout(() => {
                resolve(this.state.context.isAuthenticated);
            }, 100);
        });
    }
}

export default AuthenticationRoot;

